<template>
  <div class="patient-with-operation">
    <div class="patient-header">
      <h3>Patient {{resource.id}}</h3>
    </div>
    <div class="patient-body">
      <div class="patient-body-row">
        <rp-textinput
          v-model="resource.patient_email"
          placeholder="Email"
          :disabled="disabled"
          :error="patient_emailError"
          @input="touch('patient_email')"
          isRequired />
        <rp-datepicker
          v-model="resource.performed_at"
          placeholder="Date of Surgery"
          :disabled="disabled"
          :error="performed_atError"
          :yearToOffset="2"
          :initialDay="currentDateString.split('-')[2]"
          :initialMonth="currentDateString.split('-')[1]"
          :initialYear="currentDateString.split('-')[0]"
          @input="touch('performed_at')"
          isRequired />
        <div class="patient-body-container">
          <span>Operated Eyes:</span>
          <rp-checkbox
            v-model="isLeftEnabled"
            label="L" />
          <rp-checkbox
            v-model="isRightEnabled"
            label="R" />
        </div>
        <rp-select
          v-model="resource.hospital_id"
          placeholder="Hospital"
          :disabled="disabled"
          :error="hospital_idError"
          @input="touch('hospital_id')"
          isRequired>
          <option v-for="hospital in hospitals" :key="hospital.id" :value="hospital.id">
            {{hospital.name}}
          </option>
        </rp-select>
      </div>
      <div class="patient-body-row">
        <div class="patient-body-column">
          <div v-if="isLeftEnabled" class="patient-body-subrow patient-body-container">
            <rp-select
              v-model="leftManufacturer"
              placeholder="L IOL Manufacturer"
              :disabled="disabled"
              isRequired>
              <option v-for="iolMan in iolManufacturers" :key="iolMan.id" :value="iolMan.name">
                {{iolMan.name}}
              </option>
            </rp-select>
            <rp-select
              v-model="resource.left_iol_id"
              placeholder="L IOL Model"
              :disabled="disabled"
              isRequired>
              <option v-for="iolModel in leftIolModels" :key="iolModel.id" :value="iolModel.id">
                {{iolModel.name}}
              </option>
            </rp-select>
          </div>
          <div v-if="isRightEnabled" class="patient-body-subrow patient-body-container">
            <rp-select
              v-model="rightManufacturer"
              placeholder="R IOL Manufacturer"
              :disabled="disabled"
              isRequired>
              <option v-for="iolMan in iolManufacturers" :key="iolMan.id" :value="iolMan.name">
                {{iolMan.name}}
              </option>
            </rp-select>
            <rp-select
              v-model="resource.right_iol_id"
              placeholder="R IOL Model"
              :disabled="disabled"
              isRequired>
              <option v-for="iolModel in rightIolModels" :key="iolModel.id" :value="iolModel.id">
                {{iolModel.name}}
              </option>
            </rp-select>
          </div>
        </div>
        <div class="patient-body-column">
          <div class="patient-body-subrow patient-body-container">
            <rp-textinput
              v-model="resource.patient_id"
              placeholder="Patient ID"
              :disabled="disabled" />
            <rp-select
              v-model="resource.operation_type_id"
              placeholder="Operation Type"
              :disabled="disabled"
              :error="operation_type_idError"
              @input="touch('operation_type_id')"
              isRequired>
              <option v-for="type in operationTypes" :key="type.id" :value="type.id">
                {{type.name}}
              </option>
            </rp-select>
          </div>
          <div class="patient-body-subrow patient-body-container">
            <rp-select
              v-model="resource.locale_id"
              placeholder="Language"
              :disabled="disabled"
              :error="locale_idError"
              @input="touch('locale_id')"
              isRequired>
              <option v-for="locale in locales" :key="locale.id" :value="locale.id">
                {{locale.name}}
              </option>
            </rp-select>
            <rp-multicheck
              v-model="resource.questionnaire_ids"
              :options="questionnaireOptions"
              :text="'Questionnaires'" />
          </div>
          <div class="patient-body-subrow patient-body-container"
            v-if="trials.length > 0" >
            <!-- Add patient to trial -->
            <rp-checkbox class="trial-check"
              v-model="resource.do_trial_enrolment"
              label="Include in Study" />
            <rp-textinput
              v-model="resource.subject_id"
              placeholder="Subject ID"
              :error="subject_idError"
              :isRequired="trialFieldsRequired"
              :disabled="disabled || trialFieldsDisabled"
              @input="touch('subject_id')" />
            <rp-select
              v-model="resource.trial_id"
              placeholder="Select Study"
              :isRequired="trialFieldsRequired"
              :disabled="disabled || trialFieldsDisabled"
              @input="touch('trial_id')" >
              <option v-for="trial in trials" :key="trial.id" :value="trial.id">
                {{trial.name}}
              </option>
            </rp-select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import form from '../../../mixins/form'
  import Select from '../../../components/ui/inputs/StyledSelect'
  import TextInput from '../../../components/ui/inputs/TextInput'
  import Checkbox from '../../../components/ui/inputs/Checkbox'
  import DatePicker from '../../../components/ui/inputs/DatePicker'
  import MultiCheck from '../../../components/ui/inputs/MultiCheck'
  import {errorMessages} from '../../../api/error_messages'
  import {validationMixin} from 'vuelidate'
  import {date} from '../../../api/validators'
  import {email, required, requiredIf} from 'vuelidate/lib/validators'

  export default {
    name: 'PatientWithOperation',
    mixins: [validationMixin, form],

    components: {
      'rp-select': Select,
      'rp-textinput': TextInput,
      'rp-checkbox': Checkbox,
      'rp-datepicker': DatePicker,
      'rp-multicheck': MultiCheck,
    },

    data() {
      return {
        isLeftEnabled: false,
        isRightEnabled: false,
        leftManufacturer: '',
        rightManufacturer: '',
        trialQuestionnaires: [],
      }
    },

    mounted() {
      this.populateSelectedQuestionnaires()
    },

    props: {
      iols: Array,
      locales: Array,
      hospitals: Array,
      questionnaires: Array,
      operationTypes: Array,
      defaultQuestionnaires: Array,
      disabled: Boolean,
      trials: Array,
    },

    computed: {
      ...errorMessages([
        'patient_email',
        'locale_id',
        'hospital_id',
        'operation_type_id',
        'performed_at',
        'subject_id',
        'trial_id']),

      iolManufacturers() {
        let mans = []
        this.iols.forEach((iol, idx) => {
          if (!mans.find(x => x.name === iol.manufacturer)) {
            mans.push({id: idx, name: iol.manufacturer})
          }
        })
        return mans
      },

      leftIolModels() {
        let models = []
        this.iols.forEach((iol) => {
          if (iol.manufacturer === this.leftManufacturer) {
            models.push({id: iol.id, name: iol.model})
          }
        })
        return models
      },

      rightIolModels() {
        let models = []
        this.iols.forEach((iol) => {
          if (iol.manufacturer === this.rightManufacturer) {
            models.push({id: iol.id, name: iol.model})
          }
        })
        return models
      },

      questionnaireOptions() {
        let options = []
        this.questionnaires.forEach((q) => {
          options.push({text: q.name, value: q.id})
        })
        options.push({text: 'Welcome Email', value: 'welcome'})
        return options
      },

      trialFieldsDisabled() {
        return !this.resource.do_trial_enrolment
      },

      trialFieldsRequired() {
        return this.resource.do_trial_enrolment
      },

      currentDateString() {
        return new Date().toISOString().split('T')[0]
      },
    },

    methods: {
      populateSelectedQuestionnaires() {
        let quests = this.trialQuestionnaires.length > 0 ? this.trialQuestionnaires : this.defaultQuestionnaires
        this.resource.questionnaire_ids = []
        this.questionnaires.forEach((q) => {
          if (quests.includes(q.id) || quests.length === 0) {
            this.resource.questionnaire_ids.push(q.id)
          }
        })
        this.resource.questionnaire_ids.push('welcome')
      },
    },

    watch: {
      isLeftEnabled() {
        if (!this.isLeftEnabled) {
          this.leftManufacturer = ''
        }
      },

      isRightEnabled() {
        if (!this.isRightEnabled) {
          this.rightManufacturer = ''
        }
      },

      leftManufacturer() {
        if (this.resource.left_iol_id) {
          this.resource.left_iol_id = ''
        }
      },

      rightManufacturer() {
        if (this.resource.right_iol_id) {
          this.resource.right_iol_id = ''
        }
      },

      questionnaires() {
        this.populateSelectedQuestionnaires()
      },

      defaultQuestionnaires() {
        this.populateSelectedQuestionnaires()
      },

      operationTypes() {
        if (this.operationTypes.length > 0) {
          this.resource.operation_type_id = this.operationTypes[0].id
        }
      },

      'resource.do_trial_enrolment'() {
        if (this.resource.do_trial_enrolment === false) {
          this.resource.subject_id = ''
          this.resource.trial_id = ''
          this.trialQuestionnaires = []
        }
        this.populateSelectedQuestionnaires()
      },

      'resource.trial_id'() {
        if (this.resource.trial_id !== '') {
          let trial = this.trials.find(x => x.id === this.resource.trial_id)
          if (trial) {
            this.trialQuestionnaires = trial.questionnaires.map(x => x.id)
          }
        } else {
          this.trialQuestionnaires = []
        }
        this.populateSelectedQuestionnaires()
      },
    },

    validations: {
      resource: {
        patient_email: {required, email},
        locale_id: {required},
        hospital_id: {required},
        performed_at: {required, date},
        subject_id: {required: requiredIf(function() {
          return this.resource.do_trial_enrolment
        })},
        trial_id: {required: requiredIf(function() {
          return this.resource.do_trial_enrolment
        })},
        operation_type_id: {required},
      },
    },
  }
</script>

<style lang="sass">
  @import '../../../styles/base/variables'

  .patient-with-operation
    background: $color-panel-bg
    border-radius: 20px
    max-width: 1200px
    h3
      text-transform: none

    .patient-header
      color: $color-text
      > h3
        margin-bottom: 0

    .patient-body
      display: flex
      flex-direction: column

      .patient-body-row
        margin-top: -10px
        display: flex
        gap: 20px
        > div
          flex: 1 1 0%

        .patient-body-subrow
          margin-top: -10px
          flex: 0 0 calc(50% - 10px)

        .patient-body-column
          height: 100%
          display: flex
          flex-direction: column

        .patient-body-container
          justify-content: space-evenly
          align-items: center
          display: flex
          gap: 20px
          > span
            font-size: larger

          .trial-check
            .checkbox
              width: 130px
              font-size: 1.2em
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/PatientWithOperation.vue